import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, Tag, Percent, DollarSign, Calendar,
  Trash2, Edit2, Copy, Gift, Zap, Clock,
  CheckCircle, XCircle, Users, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PromoCodes = ({ onRefresh }) => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    discount_type: 'percent',
    discount_value: 10,
    applies_to: 'all',
    plan_ids: [],
    max_uses: null,
    max_uses_per_user: 1,
    starts_at: '',
    expires_at: '',
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [promosRes, templatesRes, plansRes] = await Promise.all([
        axios.get(`${API_URL}/api/marketing/promos?active_only=false`, { headers }),
        axios.get(`${API_URL}/api/marketing/promos/templates`, { headers }),
        axios.get(`${API_URL}/api/plans`, { headers })
      ]);

      setPromoCodes(promosRes.data.promo_codes);
      setTemplates(templatesRes.data.templates);
      setPlans(plansRes.data.plans || []);
    } catch (error) {
      console.error('Error loading promo data:', error);
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      discount_type: 'percent',
      discount_value: 10,
      applies_to: 'all',
      plan_ids: [],
      max_uses: null,
      max_uses_per_user: 1,
      starts_at: '',
      expires_at: '',
      is_active: true
    });
    setEditingPromo(null);
  };

  const openCreateModal = (template = null) => {
    if (template) {
      setFormData({
        ...formData,
        code: template.suggested_code,
        name: template.name,
        description: template.description,
        discount_type: template.discount_type,
        discount_value: template.discount_value,
        max_uses: template.max_uses || null,
        max_uses_per_user: template.max_uses_per_user || 1
      });
    } else {
      resetForm();
    }
    setShowCreateModal(true);
  };

  const openEditModal = (promo) => {
    setEditingPromo(promo);
    setFormData({
      code: promo.code,
      name: promo.name,
      description: promo.description || '',
      discount_type: promo.discount_type,
      discount_value: promo.discount_value,
      applies_to: promo.applies_to,
      plan_ids: promo.plan_ids || [],
      max_uses: promo.max_uses,
      max_uses_per_user: promo.max_uses_per_user || 1,
      starts_at: promo.starts_at ? promo.starts_at.slice(0, 16) : '',
      expires_at: promo.expires_at ? promo.expires_at.slice(0, 16) : '',
      is_active: promo.is_active
    });
    setShowCreateModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const payload = {
        ...formData,
        code: formData.code.toUpperCase(),
        starts_at: formData.starts_at ? new Date(formData.starts_at).toISOString() : null,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null
      };

      if (editingPromo) {
        await axios.put(`${API_URL}/api/marketing/promos/${editingPromo.id}`, payload, { headers });
        toast.success('Promo code updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/marketing/promos`, payload, { headers });
        toast.success('Promo code created successfully!');
      }

      setShowCreateModal(false);
      resetForm();
      loadData();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error saving promo:', error);
      toast.error(error.response?.data?.detail || 'Failed to save promo code');
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm('Are you sure you want to deactivate this promo code?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/marketing/promos/${promoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Promo code deactivated');
      loadData();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting promo:', error);
      toast.error('Failed to deactivate promo code');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Promo code copied!');
  };

  const getDiscountDisplay = (promo) => {
    switch (promo.discount_type) {
      case 'percent':
        return `${promo.discount_value}% OFF`;
      case 'fixed':
        return `$${promo.discount_value} OFF`;
      case 'free_month':
        return `${promo.discount_value} Month${promo.discount_value > 1 ? 's' : ''} FREE`;
      default:
        return `${promo.discount_value}% OFF`;
    }
  };

  const getStatusBadge = (promo) => {
    const now = new Date();
    
    if (!promo.is_active) {
      return <Badge variant="secondary" className="bg-gray-100 text-gray-600">Inactive</Badge>;
    }
    
    if (promo.starts_at && new Date(promo.starts_at) > now) {
      return <Badge variant="secondary" className="bg-blue-100 text-blue-700">Scheduled</Badge>;
    }
    
    if (promo.expires_at && new Date(promo.expires_at) < now) {
      return <Badge variant="secondary" className="bg-red-100 text-red-700">Expired</Badge>;
    }
    
    if (promo.max_uses && promo.times_used >= promo.max_uses) {
      return <Badge variant="secondary" className="bg-amber-100 text-amber-700">Limit Reached</Badge>;
    }
    
    return <Badge className="bg-green-100 text-green-700">Active</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="promo-codes">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Gift className="h-5 w-5 text-blue-600" />
            Promo Codes
          </h2>
          <p className="text-gray-600 text-sm">Create and manage promotional discounts</p>
        </div>
        <Button onClick={() => openCreateModal()} data-testid="create-promo-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Promo Code
        </Button>
      </div>

      {/* Quick Templates */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Quick Templates
          </CardTitle>
          <CardDescription>Start with a pre-built promotion template</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {templates.map((template) => (
              <Button
                key={template.id}
                variant="outline"
                className="h-auto py-3 flex flex-col items-center gap-1 hover:border-blue-500 hover:bg-blue-50"
                onClick={() => openCreateModal(template)}
              >
                <span className="text-lg font-bold text-blue-600">
                  {template.discount_type === 'percent' ? `${template.discount_value}%` :
                   template.discount_type === 'fixed' ? `$${template.discount_value}` :
                   'FREE'}
                </span>
                <span className="text-xs text-gray-600 text-center">{template.name}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Promo Codes List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Promo Codes</CardTitle>
          <CardDescription>{promoCodes.length} promo codes total</CardDescription>
        </CardHeader>
        <CardContent>
          {promoCodes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Tag className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No promo codes yet</p>
              <p className="text-sm">Create your first promo code to attract new members</p>
              <Button className="mt-4" onClick={() => openCreateModal()}>
                <Plus className="h-4 w-4 mr-2" />
                Create Promo Code
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {promoCodes.map((promo) => (
                <div
                  key={promo.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                      {promo.discount_type === 'percent' ? (
                        <Percent className="h-6 w-6 text-white" />
                      ) : promo.discount_type === 'fixed' ? (
                        <DollarSign className="h-6 w-6 text-white" />
                      ) : (
                        <Gift className="h-6 w-6 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold font-mono text-lg">{promo.code}</p>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={() => copyCode(promo.code)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        {getStatusBadge(promo)}
                      </div>
                      <p className="text-sm text-gray-600">{promo.name}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span className="font-medium text-blue-600">{getDiscountDisplay(promo)}</span>
                        {promo.max_uses && (
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {promo.times_used}/{promo.max_uses} used
                          </span>
                        )}
                        {promo.expires_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Expires {new Date(promo.expires_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono">
                      {promo.usage_count || 0} uses
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openEditModal(promo)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(promo.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-blue-600" />
              {editingPromo ? 'Edit Promo Code' : 'Create Promo Code'}
            </DialogTitle>
            <DialogDescription>
              {editingPromo ? 'Update the promo code details' : 'Create a new promotional discount code'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Code & Name */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Promo Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="SUMMER25"
                  className="font-mono uppercase"
                  required
                  disabled={!!editingPromo}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Summer Sale"
                  required
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Limited time summer promotion..."
                rows={2}
              />
            </div>

            {/* Discount Type & Value */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(value) => setFormData({ ...formData, discount_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage Off</SelectItem>
                    <SelectItem value="fixed">Fixed Amount Off</SelectItem>
                    <SelectItem value="free_month">Free Month(s)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="discount_value">
                  {formData.discount_type === 'percent' ? 'Discount %' :
                   formData.discount_type === 'fixed' ? 'Amount ($)' :
                   'Free Months'}
                </Label>
                <Input
                  id="discount_value"
                  type="number"
                  min="0"
                  max={formData.discount_type === 'percent' ? 100 : undefined}
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                  required
                />
              </div>
            </div>

            {/* Usage Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="max_uses">Max Total Uses</Label>
                <Input
                  id="max_uses"
                  type="number"
                  min="1"
                  value={formData.max_uses || ''}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_uses_per_user">Max Per User</Label>
                <Input
                  id="max_uses_per_user"
                  type="number"
                  min="1"
                  value={formData.max_uses_per_user}
                  onChange={(e) => setFormData({ ...formData, max_uses_per_user: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="starts_at">Start Date</Label>
                <Input
                  id="starts_at"
                  type="datetime-local"
                  value={formData.starts_at}
                  onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expires_at">Expiration Date</Label>
                <Input
                  id="expires_at"
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                />
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>Active</Label>
                <p className="text-sm text-gray-500">Enable this promo code</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingPromo ? 'Update Promo' : 'Create Promo'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromoCodes;
